import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const cls = "mt-1.5 bg-white/5 border-white/10";

export const InquiryForm = ({ kind, title, fields, messageLabel = "Message", messagePlaceholder, submitLabel = "Send message", successText, testId = "inquiry" }) => {
  const [form, setForm] = useState(() => Object.fromEntries([["name", ""], ["email", ""], ["pitch", ""], ...fields.map((f) => [f.id, ""])]));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.pitch.trim().length < 10) return toast.error(`${messageLabel} needs at least 10 characters`);
    setBusy(true);
    try { await api.post("/join", { ...form, kind }); setDone(true); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="card-surface rounded-3xl p-10 text-center" data-testid={`${testId}-success`}>
      <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
      <h2 className="mt-4 font-display text-2xl font-extrabold">Got it — thank you!</h2>
      <p className="mt-2 text-slate-400">{successText}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="card-surface rounded-3xl p-6 sm:p-8 space-y-5" data-testid={`${testId}-form`}>
      <h2 className="font-display text-2xl font-extrabold">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Your name *</Label><Input required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} className={cls} data-testid={`${testId}-name-input`} /></div>
        <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={cls} data-testid={`${testId}-email-input`} /></div>
        {fields.map((f) => (
          <div key={f.id} className={f.full ? "sm:col-span-2" : ""}>
            <Label>{f.label}{f.required ? " *" : ""}</Label>
            {f.options
              ? <select required={f.required} value={form[f.id]} onChange={(e) => set(f.id, e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-white/5 border border-white/10 px-3 text-sm" data-testid={`${testId}-${f.id}-input`}><option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
              : <Input required={f.required} value={form[f.id]} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} className={cls} data-testid={`${testId}-${f.id}-input`} />}
          </div>
        ))}
      </div>
      <div><Label>{messageLabel} *</Label><Textarea required rows={6} value={form.pitch} onChange={(e) => set("pitch", e.target.value)} placeholder={messagePlaceholder} className={cls} data-testid={`${testId}-message-input`} /></div>
      <Button type="submit" disabled={busy} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300 px-6" data-testid={`${testId}-submit-button`}><Send size={16} className="mr-2" /> {busy ? "Sending…" : submitLabel}</Button>
    </form>
  );
};
